import {
    BxfButton,
    BxfButtonSizeEnum,
    BxfCard,
    BxfCardGroup,
    BxfCardType,
    BxfCheckbox,
    BxfCheckboxChangedDetailInterface,
    BxfForm,
    BxfFormGroup,
    BxfFormItem,
    BxfTextArea,
    BxfTextAreaInputBlurDetailInterface,
} from '@blk/bxf-react-components';

import styled from '@emotion/styled';
import { memo } from 'react';
import { CaptureFeedbackConstants } from '../constants';
import { useFeedback } from '../hooks';
import { getKey } from '../utils';

const {
    ASTERISK,
    SLOT_CONTENT,
    SUBMIT,
    FEEDBACK_SLOT,
    DATA_CY_FEEDBACK,
    PRESENTATION,
    DATA_CY_CHECKBOX_GROUP,
    CHECKBOX_GROUP_DATA,
    FEEDBACK_CONSTANTS,
} = CaptureFeedbackConstants;

/**
 * **Question 1**: Answer Rating
 * If the rating is 4 or 5, no additional feedback is requested.
 * @param {number} index
 * @returns {JSX.Element}
 */
const RatingSection = memo(
    ({ setRating }: { setRating: (value: string, rating: number) => void }) => {
        const { values: ratings, question } = FEEDBACK_CONSTANTS.rating || {};
        return (
            <BxfCardGroup assistiveText={question} style={{ width: '100%' }}>
                <FeedbackCardGroupContainer
                    role={PRESENTATION}
                    aria-labelledby={question}
                >
                    {ratings.map((label, index) => (
                        <div role={PRESENTATION} key={label}>
                            <StackedCardGroup role={PRESENTATION}>
                                <BxfCard
                                    aria-required='true'
                                    type={BxfCardType.SELECTABLE}
                                    isSingleSelect
                                    dataLinkEvent={`chatblk | result | feedback | quality rating | ${label}`}
                                    onCardClicked={() => {
                                        setRating(label, index + 1);
                                    }}
                                    assistiveText={`${index} - ${label}`}
                                >
                                    <FeedbackCard
                                        data-cy={`data-cy-${index}-rating`}
                                        slot={SLOT_CONTENT}
                                    >
                                        {index}
                                    </FeedbackCard>
                                </BxfCard>
                                <FeedbackCardLabel>{label}</FeedbackCardLabel>
                            </StackedCardGroup>
                        </div>
                    ))}
                </FeedbackCardGroupContainer>
            </BxfCardGroup>
        );
    }
);
RatingSection.displayName = 'RatingSection';

/**
 * **Question 2**: Checkbox Group for Negative Feedback
 * @param setCheckboxAnswers - A function to update the checkbox answers, receiving the label and checked state.
 *
 * @returns A multi-column wrapper containing a group of checkboxes for user interaction.
 */
const CheckboxSection = memo(
    ({
        setCheckboxAnswers,
    }: {
        setCheckboxAnswers: (label: string, checked: boolean) => void;
    }) => {
        const question = FEEDBACK_CONSTANTS.checkbox.question;
        return (
            <MultiColumnWrapper>
                <MultiColumnCheckboxGroup
                    role='group'
                    aria-labelledby={question}
                    data-cy={DATA_CY_CHECKBOX_GROUP}
                >
                    {CHECKBOX_GROUP_DATA.map((item, i) => (
                        <BxfCheckbox
                            label={item.label}
                            key={item.label}
                            uid={`checkbox-${i}`}
                            onCheckboxChanged={(
                                e: CustomEvent<BxfCheckboxChangedDetailInterface>
                            ) => {
                                const {
                                    value: { checked, label },
                                } = e.detail;
                                setCheckboxAnswers(label, checked);
                            }}
                        />
                    ))}
                </MultiColumnCheckboxGroup>
            </MultiColumnWrapper>
        );
    }
);
CheckboxSection.displayName = 'CheckboxSection';

/**
 * Memoized component for rendering a text area section used to capture user feedback.
 *
 * @param setValue - Callback function to update the comment value when the text area loses focus.
 * @param isRewrite - Optional flag to determine if the feedback is for rewriting (uses different question and placeholder).
 *
 * Displays a `BxfTextArea` with assistive text and placeholder based on the feedback type.
 */
const TextboxSection = memo(
    ({
        setValue,
        isRewrite,
    }: {
        setValue: (comment: string) => void;
        isRewrite?: boolean;
    }) => {
        const { question, placeholder } =
            FEEDBACK_CONSTANTS[isRewrite ? 'rewrite' : 'comment'] || {};
        return (
            <div style={{ width: '100%' }}>
                <BxfTextArea
                    assistiveText={question}
                    placeholder={placeholder}
                    isHorizontalResizeDisabled
                    onInputBlur={(
                        e: CustomEvent<BxfTextAreaInputBlurDetailInterface>
                    ) => {
                        const { value } = e.detail;
                        setValue(value);
                    }}
                />
            </div>
        );
    }
);
TextboxSection.displayName = 'TextboxSection';

/**
 * Memoized component that renders a feedback section with a question header and form item.
 *
 * @param section - The key representing the feedback section, used to retrieve the question text from FEEDBACK_CONSTANTS.
 * @param children - Optional React nodes to be rendered inside the form item.
 * @returns JSX.Element containing the feedback question and associated form item.
 */
const FeedbackSection = memo(
    ({
        section,
        children,
    }: {
        section: string;
        children?: React.ReactNode;
    }): JSX.Element => {
        const { question } = FEEDBACK_CONSTANTS[section];
        return (
            <div key={getKey('feedback-question', section)}>
                <FeedbackPanelHeader>
                    {question}
                    <QuestionRequiredAsterisk>
                        {ASTERISK}
                    </QuestionRequiredAsterisk>
                </FeedbackPanelHeader>
                <BxfFormItem role={PRESENTATION}>{children}</BxfFormItem>
            </div>
        );
    }
);
FeedbackSection.displayName = 'FeedbackSection';

/**
 * A button component for submitting feedback.
 *
 * @param handleSubmitFeedback - Callback function invoked when the button is clicked.
 * @param isDisabled - Optional boolean to disable the button.
 * @returns A BxfButton configured for feedback submission.
 */
const SubmitButton = ({
    handleSubmitFeedback,
    isDisabled,
}: {
    handleSubmitFeedback: (event) => void;
    isDisabled?: boolean;
}) => {
    return (
        <BxfButton
            size={BxfButtonSizeEnum.SMALL}
            label={SUBMIT}
            isDisabled={isDisabled}
            dataLinkEvent='chatblk | result | feedback | submit'
            onClick={handleSubmitFeedback}
        />
    );
};
SubmitButton.displayName = 'SubmitButton';

/**
 * CaptureFeedback component captures user feedback through various input methods such as
 * text area, card selection, and checkbox group.
 *
 * The component uses the `useFeedback` hook to manage the feedback state and event handlers.
 * This custom feedback component is only used in pre-production environments.
 *
 * @returns {JSX.Element} The CaptureFeedback component.
 */
const CaptureFeedback = (): JSX.Element => {
    const {
        setRating,
        setCheckboxAnswers,
        setComment,
        setRewrite,
        handleSubmitFeedback,
        shouldDisplayAdditionalFeedback,
        isSubmitDisabled,
    } = useFeedback();

    return (
        <QuestionContainer data-cy={DATA_CY_FEEDBACK} slot={FEEDBACK_SLOT}>
            <BxfFormGroup>
                <FeedbackSection section={'rating'}>
                    <RatingSection setRating={setRating} />
                </FeedbackSection>
                <NegativeFeedback isVisible={shouldDisplayAdditionalFeedback}>
                    <FeedbackSection section={'checkbox'}>
                        <CheckboxSection
                            setCheckboxAnswers={setCheckboxAnswers}
                        />
                    </FeedbackSection>
                    <FeedbackSection section={'comment'}>
                        <TextboxSection setValue={setComment} />
                    </FeedbackSection>
                    <FeedbackSection section={'rewrite'}>
                        <TextboxSection setValue={setRewrite} isRewrite />
                    </FeedbackSection>
                </NegativeFeedback>
                <SubmitButton
                    isDisabled={isSubmitDisabled}
                    handleSubmitFeedback={handleSubmitFeedback}
                />
            </BxfFormGroup>
        </QuestionContainer>
    );
};

// Styled Components
const FeedbackPanelHeader = styled.p`
    font-family:
        'BLK Fort',
        -apple-system,
        BlinkMacSystemFont,
        'Segoe UI',
        'Roboto',
        'Oxygen',
        'Ubuntu',
        'Fira Sans',
        'Helvetica Neue',
        sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: get-line-height(13, 20);
    margin-top: 12px;
    margin-bottom: 16px;
    font-feature-settings:
        'clig' off,
        'liga' off;
`;

const FeedbackCard = styled.div`
    display: flex !important;
    height: 8px;
    align-items: center;
    justify-content: center;
`;

const FeedbackCardLabel = styled.div`
    width: inherit;
    margin-top: 8px;
    text-align: center;
`;

const QuestionRequiredAsterisk = styled.span`
    padding-left: 4px;
    color: #ff4713;
    font-size: 16px;
    line-height: 1.1875;
    padding-bottom: 4px;
`;

const FeedbackCardGroupContainer = styled.div`
    display: flex;
    justify-content: space-between;
    margin: 4px;
`;

const QuestionContainer = styled(BxfForm)`
    margin-top: 24px;
`;

const StackedCardGroup = styled.div`
    width: 4rem;
    font-weight: 700;
    font-size: 12px;
`;

const NegativeFeedback = styled.div<{ isVisible?: boolean }>`
    display: ${(props) => (props.isVisible ? 'block' : 'none')};
`;

const MultiColumnWrapper = styled.div`
    font-family:
        'BLK Fort',
        -apple-system,
        BlinkMacSystemFont,
        'Segoe UI',
        'Roboto',
        'Oxygen',
        'Ubuntu',
        'Fira Sans',
        'Helvetica Neue',
        sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: get-line-height(13, 16);
    color: $blk-gray-40;
`;

const MultiColumnCheckboxGroup = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
`;

export default memo(CaptureFeedback);
